import React from "react";
import { canSetInspectionData, setInspectionData, useInspectionData } from "./inspection_display.js";
import { authorizedFetch } from "./boot.js";
import { Card } from "./ui.jsx";
import { LotIdentity } from "./lot_identity.jsx";

const ZONE = "Australia/Sydney";
export const stamp = value => typeof value === "string" && /(?:Z|[+-]\d{2}:\d{2})$/i.test(value)
  && Number.isFinite(Date.parse(value)) ? new Date(value) : null;
const dateLabel = value => new Intl.DateTimeFormat("en-AU", {
  timeZone: ZONE, weekday: "short", day: "numeric", month: "short",
}).format(value);
const timeLabel = value => new Intl.DateTimeFormat("en-AU", {
  timeZone: ZONE, hour: "numeric", minute: "2-digit",
}).format(value);
const dayKey = value => new Intl.DateTimeFormat("en-CA", {
  timeZone: ZONE, year: "numeric", month: "2-digit", day: "2-digit",
}).format(value);

export function saleCountdown(startDate, now) {
  // A date-only sale start is a venue calendar day, never browser-local midnight.
  const value = typeof startDate === "string" ? startDate.slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T12:00:00+11:00`);
  if (!Number.isFinite(date.getTime()) || dayKey(date) !== value) return null;
  const days = Math.round((Date.parse(`${value}T00:00:00Z`) - Date.parse(`${dayKey(now)}T00:00:00Z`)) / 86400000);
  return { days, label: dateLabel(date) };
}

// Panels have independent grants and failure states. A failed refresh clears
// private data; cancelled requests can never repopulate a different realm.
function useRead(path, sale, vendor, retry, repository = false) {
  const [state, setState] = React.useState({ loading: Boolean(path), data: null, error: false });
  React.useEffect(() => {
    if (!path) { setState({ loading: false, data: null, error: false }); return; }
    const controller = new AbortController();
    let active = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await authorizedFetch(path, { signal: controller.signal });
        const data = await response.json();
        if (!response.ok || (repository ? data.saleId !== sale || data.source === "error"
          : data.sale !== sale || data.vendor !== vendor)) throw new Error("Unavailable");
        if (active) setState({ loading: false, data, error: false });
      } catch (error) {
        if (active && error?.name !== "AbortError") setState({ loading: false, data: null, error: true });
      } finally { inFlight = false; }
    };
    setState({ loading: true, data: null, error: false });
    load();
    const refresh = () => { if (document.visibilityState !== "hidden") load(); };
    const interval = setInterval(refresh, repository ? 300000 : 60000);
    document.addEventListener("visibilitychange", refresh);
    return () => { active = false; controller.abort(); clearInterval(interval); document.removeEventListener("visibilitychange", refresh); };
  }, [path, sale, vendor, retry, repository]);
  return state;
}

function ReadState({ state, label }) {
  return state.loading ? <p role="status">Loading {label}…</p>
    : state.error ? <p role="status">{label} is temporarily unavailable. Use Refresh to try again.</p> : null;
}

export function HomeView({ go }) {
  const cfg = window.VD.home || {};
  const sale = cfg.sale || "26R2R";
  const vendor = window.VD.access?.realm || "";
  const sections = window.VD.sections || [];
  const [retry, setRetry] = React.useState(0);
  const [allTimes, setAllTimes] = React.useState(false);
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);
  const query = new URLSearchParams({ sale, vendor });
  const read = ref => `/collections/${ref}/?${query}`;
  const home = useRead(cfg.enabled ? read("vendor.home@1") : null, sale, vendor, retry);
  const boxes = useRead(cfg.enabled && window.VD.boxAssignments?.enabled ? read("vendor.box_assignments.get@1") : null, sale, vendor, retry);
  // Not fetched at all when the figures are hidden: reading a list this page
  // has already decided not to render would be a request nobody asked for.
  const showInspections = useInspectionData();
  // A refused write must say so. Swallowing it would leave an Owner pressing a
  // control that appears to do nothing, which is the failure mode the rest of
  // this app is careful to avoid.
  const [displayBusy, setDisplayBusy] = React.useState(false);
  const [displayError, setDisplayError] = React.useState("");
  const changeInspectionDisplay = async (show) => {
    if (displayBusy) return;
    setDisplayBusy(true); setDisplayError("");
    try { await setInspectionData(show); }
    catch (failure) {
      setDisplayError(failure.status === 409 || failure.status === 412
        ? "That setting changed somewhere else. Reload before changing it again."
        : "The setting could not be saved. Try again.");
    }
    finally { setDisplayBusy(false); }
  };
  const inspections = useRead(cfg.enabled && window.VD.inspections?.enabled && showInspections ? read("vendor.inspections.recent@1") : null, sale, vendor, retry);
  const stats = useRead(cfg.enabled && sections.includes("stats") ? "/ui/repository-stats@1" : null, sale, vendor, retry, true);
  if (!cfg.enabled) return <Card title="Home"><p>Home is not available for this account.</p></Card>;
  const data = home.data;
  const active = (data?.lots || []).filter(lot => !lot.withdrawn);
  const unsold = active.filter(lot => !lot.result);
  const timed = unsold.map(lot => ({ ...lot, moment: stamp(lot.ringSellTime) }))
    .filter(lot => lot.moment).sort((a, b) => a.moment - b.moment || a.number.localeCompare(b.number, undefined, { numeric: true }));
  const first = timed.find(lot => lot.moment >= now);
  const countdown = saleCountdown(data?.startDate, now);
  const assigned = boxes.data?.lots.filter(lot => !lot.withdrawn && lot.box).length;
  const recent = (inspections.data?.inspections || []).filter(row => !row.voided).slice(0, 5);
  // Every open, as Stats and the Repository's portal count them (24 Sep 2026):
  // the vendor's lots' view-counts figures summed, never the summary's merged
  // totals. A lot the counts did not name is listed, not counted as nothing.
  const counted = stats.data?.viewCounts;
  const everyOpen = counted && Object.values(counted.lots || {}).reduce(
    (sum, lot) => ({ xray: sum.xray + lot.xray, scope: sum.scope + lot.scope, report: sum.report + lot.report }),
    { xray: 0, scope: 0, report: 0 });
  const uncounted = counted ? (stats.data.lots || []).filter(lot => !counted.lots?.[lot.lotId]) : [];
  const countedAt = stamp(counted?.observedAt);
  const lastFetch = stamp(data?.mirrorFetchedAt);
  const missing = unsold.filter(lot => !stamp(lot.ringSellTime));
  const sessions = data?.sessions || [];
  const deadlines = data?.deadlines || [];
  return <div className="home-view fade-in">
    <div className="home-toolbar">
      <span className="caption">{data?.vendorName || window.VD.access?.realmName} · {sale}</span>
      <button type="button" className="btn sm" onClick={() => setRetry(value => value + 1)}>Refresh</button>
    </div>
    <ReadState state={home} label="Your sale"/>
    {data && <>
      <section className="home-hero" aria-label="Your sale">
        <div>
          <div className="eyebrow">{data.location || "Your sale"}</div>
          <h2>{data.saleName || sale}</h2>
          <p>{active.length} horses in your draft{data.lots.length > active.length ? ` · ${data.lots.length - active.length} withdrawn` : ""}.</p>
          <div className="home-actions">
            {sections.includes("inspect") && <button className="btn" onClick={() => go("inspect")}>Record inspection</button>}
            {sections.includes("stable") && <button className="btn" onClick={() => go("stable")}>Your boxes</button>}
            {sections.includes("live") && <button className="btn" onClick={() => go("live")}>Watch cameras</button>}
          </div>
        </div>
        <div className="home-next">
          {countdown && <p>{countdown.days > 0 ? `${countdown.days} days to sale` : countdown.days === 0 ? "Sale day" : "Sale started"} · {countdown.label}</p>}
          <span className="eyebrow">Your next published ring time</span>
          {first ? <>
            <div className="home-ring-time">{timeLabel(first.moment)}</div>
            <p>{dateLabel(first.moment)} · Sydney time</p>
            <LotIdentity lot={first}/>
          </> : <p>{timed.length ? "No future ring times published." : "Ring times not yet published."}</p>}
        </div>
      </section>
      <div className="home-summary">
        <div><span className="caption">Horses in your draft</span><strong>{active.length}</strong></div>
        {window.VD.boxAssignments?.enabled && <div><span className="caption">Boxes assigned</span><strong>{boxes.data ? `${assigned} / ${active.length}` : "—"}</strong><ReadState state={boxes} label="Box progress"/></div>}
        <div><span className="caption">Horses with a result</span><strong>{active.filter(lot => lot.result).length}</strong><span className="caption">As recorded in the catalogue</span></div>
      </div>
      <div className="home-columns">
        <Card title="Sale information" subtitle="Published by Inglis · Sydney time">
          <h3>Sessions</h3>
          {sessions.map((session, index) => {
            const starts = stamp(session.startsAt);
            return <div key={`${session.label || "session"}-${index}`} className="home-ring-row">
              <div><strong>{session.label || (session.day ? `Day ${session.day}` : "Session")}</strong>
                {starts && <span className="caption">{dateLabel(starts)} · {timeLabel(starts)}</span>}
              </div>
              <div>{session.description && <span>{session.description}</span>}
                {!starts && <span className="caption">Start time not published.</span>}
              </div>
            </div>;
          })}
          {!sessions.length && <p>No sessions published yet.</p>}
          <h3>Deadlines</h3>
          {deadlines.map((deadline, index) => {
            const due = stamp(deadline.dueAt);
            return <div key={`${deadline.label}-${index}`} className="home-ring-row">
              <div><strong>{deadline.label}</strong>{due && <span className="caption">{dateLabel(due)} · {timeLabel(due)}</span>}</div>
              <div>{deadline.description && <span>{deadline.description}</span>}</div>
            </div>;
          })}
          {!deadlines.length && <p>No deadlines published yet.</p>}
        </Card>
        <Card title="Your ring times" subtitle="Published by Inglis · Sydney time">
          <p className="caption">Times may change. Check the sale ring for the current running order.</p>
          {(allTimes ? timed : timed.slice(0, 6)).map(lot => <div key={lot.id} className="home-ring-row">
            <div><time dateTime={lot.ringSellTime}>{timeLabel(lot.moment)}</time><span className="caption">{dateLabel(lot.moment)}</span></div>
            <div><LotIdentity lot={lot}/>{lot.moment < now && <span className="caption">Time passed · result not yet recorded</span>}</div>
          </div>)}
          {timed.length > 6 && <button type="button" className="btn sm" onClick={() => setAllTimes(value => !value)}>{allTimes ? "Show fewer" : `Show all ${timed.length} ring times`}</button>}
          {missing.length > 0 && <p className="caption">Time not yet published: {missing.map(lot => `Lot ${lot.number}`).join(", ")}.</p>}
          {!unsold.length && <p>No horses awaiting a result.</p>}
          <p className="caption">{lastFetch ? `Catalogue last checked ${dateLabel(lastFetch)} at ${timeLabel(lastFetch)} Sydney time.` : "Catalogue update time unavailable."}</p>
          {data.mirrorOutcome && !["APPLIED", "UNCHANGED"].includes(data.mirrorOutcome) && <p className="caption">The latest catalogue check did not complete successfully. Ring times may be out of date.</p>}
        </Card>
        {window.VD.inspections?.enabled && !showInspections && canSetInspectionData()
          && <Card title="Inspections">
            <p className="muted">Inspection data is hidden for everyone at this vendor. Visits can still be logged.</p>
            <button type="button" className="btn sm" disabled={displayBusy}
              onClick={() => changeInspectionDisplay(true)}>Show inspection data</button>
            {displayError && <p role="alert" className="error-text">{displayError}</p>}
          </Card>}
        {window.VD.inspections?.enabled && showInspections && <Card title="Recent inspections" right={<span className="row" style={{ gap: 8 }}>
          {canSetInspectionData() && <button type="button" className="btn sm" disabled={displayBusy}
            onClick={() => changeInspectionDisplay(false)}>Hide inspection data</button>}
          <button className="more" onClick={() => go("inspect")}>View inspections</button>
        </span>}>
          {displayError && <p role="alert" className="error-text">{displayError}</p>}
          <ReadState state={inspections} label="Recent inspections"/>
          {inspections.data && !recent.length && <p>No inspections logged yet.</p>}
          {recent.map(row => <div key={row.id} className="home-inspection">
            <strong>{row.buyer}</strong><span>{row.lots.map(lot => `Lot ${lot.number}`).join(", ")}{row.agent ? ` · ${row.agent}` : ""}</span>
            <span className="caption">{stamp(row.inspectedAt) ? `${dateLabel(stamp(row.inspectedAt))} · ${timeLabel(stamp(row.inspectedAt))} Sydney time` : "Time unavailable"}</span>
          </div>)}
        </Card>}
      </div>
      {sections.includes("stats") && <Card title="Repository viewing" subtitle="Since listing" right={<button className="more" onClick={() => go("stats")}>View Stats</button>}>
        <ReadState state={stats} label="Repository viewing"/>
        {stats.data && <div className="home-summary home-repository">
          {[["X-ray views", "xray"], ["Scope views", "scope"], ["Report views", "report"]].map(([label, key]) => <div key={key}><span className="caption">{label}</span><strong>{everyOpen ? everyOpen[key].toLocaleString() : "—"}</strong></div>)}
        </div>}
        {stats.data && <p className="caption">{stats.data.stale ? "Last good snapshot · refresh is delayed. " : ""}{counted
          ? `Counts as of ${countedAt ? `${dateLabel(countedAt)} ${timeLabel(countedAt)} Sydney time` : "—"}`
          : "Every-open view counts have not been read yet."}</p>}
        {uncounted.length > 0 && <p className="caption">Not yet counted: {uncounted.map(lot => `Lot ${lot.lotNumber || lot.lotId}`).join(", ")}.</p>}
      </Card>}
    </>}
  </div>;
}
